import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { g100LogsApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { cn } from '@/lib/utils'
import type { G100Log, G100LogChannel } from '@/types'

// #224 — superuser "G100 Logs": one timeline of every exchange a G100 camera has
// with our servers, from the first HTTPS call to the detection hand-off. Built
// like "Camera Logs" (#059). The API (IsSuperUser) is the real gate; this page
// is also behind <SuperuserRoute>.

const CHANNELS: { value: '' | G100LogChannel; label: string }[] = [
  { value: '', label: 'all channels' },
  { value: 'bootstrap', label: 'bootstrap (first call)' },
  { value: 'certificate', label: 'certificate download' },
  { value: 'connection', label: 'MQTT connection' },
  { value: 'mqtt', label: 'MQTT messages' },
  { value: 'upload', label: 'uploads' },
  { value: 'ingest', label: 'detection hand-off' },
]
const POLL_MS = 10_000

export function G100LogsPage() {
  const [imei, setImei] = useState('')
  const [channel, setChannel] = useState('')
  const [onlyProblems, setOnlyProblems] = useState(false)
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState<number | null>(null)

  const params = {
    imei: imei.trim() || undefined,
    channel: channel || undefined,
    ok: onlyProblems ? 'false' : undefined,
    page,
  }
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.g100Logs(params),
    queryFn: () => g100LogsApi.list(params),
    refetchInterval: POLL_MS,
    placeholderData: (prev) => prev,
  })

  const rows = data?.results ?? []
  const total = data?.count ?? 0
  const pages = Math.max(1, Math.ceil(total / 20))

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-muted-foreground">
          {isFetching ? 'refreshing…' : `${total} exchanges`} · live (every {POLL_MS / 1000}s) · newest first
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <input
            value={imei}
            onChange={(e) => { setImei(e.target.value); setPage(1) }}
            placeholder="Filter IMEI"
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          />
          <select
            aria-label="Channel"
            value={channel}
            onChange={(e) => { setChannel(e.target.value); setPage(1) }}
            className="rounded border border-border bg-background px-2 py-1 text-sm"
          >
            {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={onlyProblems}
                   onChange={(e) => { setOnlyProblems(e.target.checked); setPage(1) }} />
            refusals and errors only
          </label>
          <button onClick={() => refetch()} className="rounded border border-border px-2 py-1 text-sm">
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load logs. Are you a superuser?</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No exchanges match.</p>
      ) : (
        <div className="overflow-x-auto rounded border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">IMEI</th>
                <th className="px-3 py-2">Channel</th>
                <th className="px-3 py-2">Direction</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2">What happened</th>
                <th className="px-3 py-2">Address</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <Row key={r.id} row={r} open={open === r.id}
                     onToggle={() => setOpen(open === r.id ? null : r.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3 text-sm">
        <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40">Prev</button>
        <span>Page {page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                className="rounded border border-border px-2 py-1 disabled:opacity-40">Next</button>
      </div>
    </div>
  )
}

function timeWithMs(iso: string) {
  const d = new Date(iso)
  return `${d.toLocaleString()}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

function Result({ ok }: { ok: boolean | null }) {
  if (ok === null) return <span className="text-muted-foreground">—</span>
  return (
    <span className={cn('rounded px-1.5 py-0.5 text-xs',
      ok ? 'bg-green-500/15 text-green-700 dark:text-green-400'
         : 'bg-red-500/15 text-red-700 dark:text-red-400')}>
      {ok ? 'ok' : 'refused'}
    </span>
  )
}

function pretty(payload: string) {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2)
  } catch {
    return payload
  }
}

function Row({ row, open, onToggle }: { row: G100Log; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer border-t border-border hover:bg-muted/30">
        <td className="px-3 py-2 whitespace-nowrap">{timeWithMs(row.created_at)}</td>
        <td className="px-3 py-2 font-mono text-xs">{row.imei || '—'}</td>
        <td className="px-3 py-2">{row.channel}</td>
        <td className="px-3 py-2 whitespace-nowrap">
          {row.direction === 'out' ? 'server → camera' : 'camera → server'}
        </td>
        <td className="px-3 py-2"><Result ok={row.ok} /></td>
        <td className="px-3 py-2">{row.summary}</td>
        <td className="px-3 py-2 font-mono text-xs">{row.remote_addr || '—'}</td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-muted/20">
          <td colSpan={7} className="px-3 py-3 space-y-3">
            <Field label="Topic / path">{row.topic || '—'}</Field>
            <Field label="Payload (verbatim)">
              <pre className="whitespace-pre-wrap">{row.payload ? pretty(row.payload) : '—'}</pre>
            </Field>
          </td>
        </tr>
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground">{label}</div>
      <div className="font-mono text-xs">{children}</div>
    </div>
  )
}
